import { TabMenu } from 'primereact/tabmenu';
import { type MenuItem } from 'primereact/menuitem';
import { useLocation, useNavigate } from 'react-router-dom';
import { Outlet } from 'react-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import OpenAPI from '../../endpoint/Openapi';
import useTenantStore from '../../stores/TenantStore';
import { type components } from '../../endpoint/Schema';
import { Toast } from 'primereact/toast';

type LatestRelease = components['schemas']['AppReleaseDto'];

function AppCustomisation() {
  const toast = useRef<Toast>(null);
  const show = () => {
    if (toast.current)
      toast.current.show({
        severity: 'info',
        summary: 'Info',
        detail: 'Copied!',
      });
  };
  const navigate = useNavigate();
  const location = useLocation();
  const [activeIndex, setActiveIndex] = useState(0);
  const [latestRelease, setLatestRelease] = useState<LatestRelease | null>(
    null,
  );

  const tenantId = useTenantStore((state) => state.id);
  const featureSettings = useTenantStore((state) => state.featureSettings);

  const menuModel = useMemo<MenuItem[]>(() => {
    const baseItems: MenuItem[] = [
      {
        id: 'features',
        label: 'Features',
        command() {
          navigate('features');
        },
        data: { fullPath: '/app-customisation/features' },
      },
      {
        id: 'look-and-feel',
        label: 'Look and Feel',
        command() {
          navigate('look-and-feel');
        },
        data: { fullPath: '/app-customisation/look-and-feel' },
      },
      {
        id: 'store-configuration',
        label: 'Store Configuration',
        command() {
          navigate('store-configuration');
        },
        data: { fullPath: '/app-customisation/store-configuration' },
      },
    ];

    // Add white-list-registration tab if RestrictToSpecialUsers is enabled
    if (
      featureSettings?.registrationSettings?.registrationMode ===
        'AllowAllButFlagNoneWhiteList' ||
      featureSettings?.registrationSettings?.registrationMode ===
        'AllowOnlyWhiteList'
    ) {
      baseItems.push({
        id: 'white-list-registration',
        label: 'White List Registration',
        command() {
          navigate('white-list-registration');
        },
        data: { fullPath: '/app-customisation/white-list-registration' },
      });
    }

    return baseItems;
  }, [
    navigate,
    featureSettings?.registrationSettings?.registrationMode !== 'AllowAll',
  ]);

  useEffect(() => {
    const foundIndex =
      menuModel
        .map((item, index) => ({ item, index }))
        .filter(({ item }) => location.pathname.startsWith(item.data.fullPath))
        .pop()?.index ?? -1;

    setActiveIndex(foundIndex);

    if (
      !menuModel.some((item) =>
        location.pathname.startsWith(item.data.fullPath),
      )
    )
      navigate('features');
  }, [menuModel, location.pathname, navigate]);

  useEffect(() => {
    const fn = async () => {
      if (!tenantId) {
        return;
      }

      const applications = await OpenAPI.GET('/api/v2/admin/application', {
        params: {
          query: {
            tenantId,
          },
        },
      });

      if (!applications.data) {
        return;
      }
      const applicationId = applications.data.id;
      if (!applicationId) {
        return;
      }

      const lr = (
        await OpenAPI.GET(
          '/api/v2/admin/application/{applicationId}/last-release',
          {
            params: {
              path: { applicationId },
              query: {
                tenantId,
              },
            },
          },
        )
      ).data;
      if (!lr) {
        return;
      }
      setLatestRelease(lr);
    };
    void fn();
  }, [tenantId]);
  return (
    <div className="flex flex-col gap-10 w-full">
      <Toast ref={toast} />
      <div className="flex flex-col text-right">
        <span
          className="mr-2 underline cursor-pointer"
          onClick={() => {
            if (latestRelease?.androidApkLink) {
              void navigator.clipboard.writeText(latestRelease?.androidApkLink);
              show();
            }
          }}
        >
          {latestRelease?.androidApkLink
            ? 'Copy Android app Download Link'
            : `Android app download link will be available soon ${latestRelease?.runState === 'InProgress' ? '✅' : '❌'}`}
        </span>

        <span
          className="mr-2 underline cursor-pointer"
          onClick={() => {
            if (latestRelease?.iosBetaTesterLink) {
              void navigator.clipboard.writeText(
                latestRelease?.iosBetaTesterLink,
              );
              show();
            }
          }}
        >
          {latestRelease?.iosBetaTesterLink
            ? 'Copy TestFlight Link'
            : `TestFlight Link will be available soon ${latestRelease?.runState === 'InProgress' ? '✅' : '❌'}`}
        </span>
      </div>
      <TabMenu
        model={menuModel}
        className="text-3xl w-full"
        activeIndex={activeIndex}
        onTabChange={(e) => {
          setActiveIndex(e.index);
        }}
      />
      <Outlet />
    </div>
  );
}

export default AppCustomisation;
